import professorImage from "@/assets/professor.svg";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ArticleListItem, useApiClient } from "@/lib/api";
import { ArrowRight, BookOpen, Clock, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Category color mapping - hash-based generation for consistent, well-separated colors
const getCategoryColor = (category: string): string => {
  // Generate a hash from the category name
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use hash to generate hue (0-360) with better distribution
  // Multiply by a large prime to spread out values
  const hue = Math.abs(hash * 137.508) % 360;
  
  // Use fixed saturation and lightness for good contrast and readability
  // Saturation: 65% (vibrant but not too intense)
  // Lightness: 45% (dark enough for white text, light enough to be visible)
  return `hsl(${hue}, 65%, 45%)`;
};

// Cookie helpers for tracking read articles
const READ_ARTICLES_COOKIE = "coach_u_read_articles";

const getReadArticles = (): Set<string> => {
  if (typeof document === "undefined") return new Set();
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${READ_ARTICLES_COOKIE}=`));
  if (!cookie) return new Set();
  try {
    const value = cookie.split("=")[1];
    const slugs = JSON.parse(decodeURIComponent(value));
    return new Set(Array.isArray(slugs) ? slugs : []);
  } catch {
    return new Set();
  }
};

const markArticleAsRead = (slug: string) => {
  if (typeof document === "undefined") return;
  const readArticles = getReadArticles();
  readArticles.add(slug);
  // Store for 1 year
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${READ_ARTICLES_COOKIE}=${encodeURIComponent(
    JSON.stringify(Array.from(readArticles))
  )}; expires=${expires.toUTCString()}; path=/`;
};

interface LearnProps {
  mode?: "private" | "public";
  withDashboard?: boolean;
}

const Learn = ({ mode = "private", withDashboard = true }: LearnProps) => {
  const { api } = useApiClient();
  const navigate = useNavigate();
  
  const [articles, setArticles] = useState<ArticleListItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const fetchArticles = useCallback(async (category?: string) => {
    setIsLoading(true);
    try {
      const response = mode === "public"
        ? await api.getPublicArticles(category || undefined)
        : await api.getArticles(category || undefined);
      setArticles(response.articles);
      setCategories(response.categories);
    } catch (error) {
      console.error('Failed to fetch articles:', error);
      toast.error('Failed to load articles');
      setArticles([]);
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, mode]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const handleCategoryChange = (category: string | null) => {
    setSelectedCategory(category);
    fetchArticles(category || undefined);
    setSearchQuery(""); // Clear search when changing category
  };

  const handleArticleClick = (article: ArticleListItem) => {
    if (mode === "public" && !article.is_public) {
      navigate(`/login?returnUrl=${encodeURIComponent(`/learn/${article.slug}`)}`);
      return;
    }
    navigate(mode === "public" ? `/public-learn/${article.slug}` : `/learn/${article.slug}`);
  };

  // Filter articles by search query
  const filteredArticles = useMemo(() => {
    if (!searchQuery.trim()) return articles;
    
    const query = searchQuery.toLowerCase();
    return articles.filter(article => 
      article.title.toLowerCase().includes(query) ||
      article.description?.toLowerCase().includes(query) ||
      article.category.toLowerCase().includes(query)
    );
  }, [articles, searchQuery]);

  // Get read articles from cookies
  const readArticles = useMemo(() => getReadArticles(), []);

  // Featured article selection logic:
  // 1. Random from filtered articles
  // 2. Never pick part 2+ (has previous_article) unless previous was read
  // 3. Never pick already read articles
  // 4. If previous was read and next exists, can feature next
  const featuredArticle = useMemo(() => {
    if (filteredArticles.length === 0) return null;

    // Filter eligible articles
    const eligible = filteredArticles.filter((article) => {
      if (mode === "public" && !article.is_public) return false;
      // Skip if already read
      if (readArticles.has(article.slug)) return false;

      // If it's part 2+ (has previous_article)
      if (article.previous_article) {
        // Only eligible if previous article was read
        return readArticles.has(article.previous_article);
      }

      // Part 1 articles are always eligible
      return true;
    });

    if (eligible.length === 0) {
      // Fallback: if no eligible articles, just pick any unread
      const unread = filteredArticles.filter(
        (a) => !readArticles.has(a.slug)
      );
      if (unread.length === 0) return null;
      const randomIndex = Math.floor(Math.random() * unread.length);
      return unread[randomIndex];
    }

    // Pick random from eligible
    const randomIndex = Math.floor(Math.random() * eligible.length);
    return eligible[randomIndex];
  }, [filteredArticles, readArticles, mode]);

  // Other articles (excluding featured)
  const otherArticles = useMemo(() => {
    if (!featuredArticle) return filteredArticles;
    return filteredArticles.filter(a => a.slug !== featuredArticle.slug);
  }, [filteredArticles, featuredArticle]);

  const content = (
    <div className="w-full px-4 sm:px-6 md:px-8 lg:px-8 py-4 sm:py-6 md:py-8 max-w-full overflow-x-hidden" style={{ boxSizing: 'border-box' }}>
        {/* Hero Section */}
        <div className="flex flex-col sm:flex-row items-start gap-4 mb-6 sm:mb-8">
          {/* Professor Logo */}
          <div className="flex-shrink-0">
            <img 
              src={professorImage} 
              alt="CoachU Professor" 
              className="w-20 h-20 sm:w-24 sm:h-24 md:w-32 md:h-32 object-contain"
            />
          </div>
          
          {/* Text Content */}
          <div className="flex-1">
            <h1 className={`${mode === "public" ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"} font-bold text-gray-900 mb-2`}>
              <span className="text-[#4f9ecb] font-bold">Coach</span><span className="text-[#334763] font-bold">U</span>
            </h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Welcome to <span className="font-semibold text-[#4f9ecb]">Coach</span><span className="font-semibold text-[#334763]">U</span>, where confidence is the curriculum. Study smarter and elevate every part of your job search.
            </p>
            <p className="text-gray-600 text-xs sm:text-sm mt-1">
              Browse on-demand lessons on salary negotiation, resumes, interviewing, and more.
            </p>
          </div>
        </div>

        
        {/* Featured Article - Above Search */}
        {!isLoading && featuredArticle && (
          <div className="mb-6 sm:mb-8">
            <Card 
              className="overflow-hidden border-2 transition-all cursor-pointer group"
              style={{ borderColor: `${getCategoryColor(featuredArticle.category)}40` }}
              onClick={() => handleArticleClick(featuredArticle)}
            >
              <div 
                className="p-4 sm:p-6 md:p-8"
                style={{ background: `linear-gradient(to bottom right, ${getCategoryColor(featuredArticle.category)}10, ${getCategoryColor(featuredArticle.category)}05)` }}
              >
                <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
                  <div className="flex-1">
                    <div className="mb-3">
                      <span 
                        className="inline-block px-3 py-1 text-white text-xs font-semibold rounded-full"
                        style={{ backgroundColor: getCategoryColor(featuredArticle.category) }}
                      >
                        {featuredArticle.category}
                      </span>
                    </div>
                    <h2 
                      className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-3 transition-colors"
                      style={{ 
                        color: featuredArticle.category ? undefined : getCategoryColor(featuredArticle.category)
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = getCategoryColor(featuredArticle.category);
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '';
                      }}
                    >
                      {featuredArticle.title}
                    </h2>
                    {featuredArticle.description && (
                      <p className="text-gray-700 text-sm sm:text-base md:text-lg mb-4 leading-relaxed">
                        {featuredArticle.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        ~8 min read
                      </span>
                    </div>
                    <div 
                      className="flex items-center font-semibold group-hover:gap-2 transition-all"
                      style={{ color: getCategoryColor(featuredArticle.category) }}
                    >
                      Start learning
                      <ArrowRight className="h-5 w-5 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6 sm:mb-8">
          <div className="relative max-w-2xl">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search CoachU..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base border-gray-300 focus:border-[#4f9ecb] focus:ring-[#4f9ecb]"
            />
          </div>
        </div>

        {/* Category Filters */}
        {categories.length > 0 && (
          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => handleCategoryChange(null)}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0
                  ${selectedCategory === null
                    ? 'bg-[#4f9ecb] text-white shadow-md'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-[#4f9ecb] hover:text-[#4f9ecb]'
                  }
                `}
              >
                All
              </button>
              {categories.map((category) => {
                const categoryColor = getCategoryColor(category);
                return (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    className={`
                      px-4 py-2 rounded-full text-sm font-medium transition-all flex-shrink-0
                      ${selectedCategory === category
                        ? 'text-white shadow-md'
                        : 'bg-white text-gray-700 border border-gray-300 hover:text-white'
                      }
                    `}
                    style={
                      selectedCategory === category
                        ? { backgroundColor: categoryColor }
                        : { 
                            borderColor: categoryColor,
                          } as React.CSSProperties
                    }
                    onMouseEnter={(e) => {
                      if (selectedCategory !== category) {
                        e.currentTarget.style.backgroundColor = categoryColor;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedCategory !== category) {
                        e.currentTarget.style.backgroundColor = '';
                      }
                    }}
                  >
                    {category}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-6">
            {/* Featured skeleton */}
            <Card className="p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </Card>
            {/* Grid skeletons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-20 mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-full mb-3"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredArticles.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchQuery ? "No lessons match your search" : "No lessons found"}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {searchQuery
                ? "Try a different search term or browse all categories."
                : "Check back soon for new lessons."}
            </p>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory(null);
                  fetchArticles();
                }}
              >
                Clear filters
              </Button>
            )}
          </div>
        )}

        {/* Content */}
        {!isLoading && filteredArticles.length > 0 && (
          <div className="space-y-6">
            {/* Article Count */}
            {otherArticles.length > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <h3 className="text-xl font-semibold text-gray-900">
                  All Lessons
                </h3>
                <span className="text-sm text-gray-600">
                  Showing {otherArticles.length} of {articles.length} {articles.length === 1 ? 'lesson' : 'lessons'}
                </span>
              </div>
            )}

            {/* Article Grid */}
            {otherArticles.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...otherArticles]
                  .sort((a, b) => {
                    if (mode !== "public") return 0;
                    return Number(Boolean(b.is_public)) - Number(Boolean(a.is_public));
                  })
                  .map((article) => {
                  const categoryColor = getCategoryColor(article.category);
                  return (
                    <Card
                      key={article.slug}
                      className="relative overflow-hidden bg-white border border-gray-200 hover:shadow-lg transition-all cursor-pointer group h-full flex flex-col"
                      style={{
                        borderColor: article.category ? undefined : categoryColor + '40'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = categoryColor + '60';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '';
                      }}
                      onClick={() => handleArticleClick(article)}
                    >
                      {mode === "public" && !article.is_public && (
                        <div className="absolute top-5 -right-10 min-w-[8.5rem] rotate-45 bg-[#4f9ecb] text-white text-[10px] font-bold uppercase py-1 text-center leading-none shadow-md z-10 whitespace-nowrap">
                          MEMBERS
                        </div>
                      )}
                      <div className="p-6 flex flex-col flex-1">
                        {/* Category Chip */}
                        <div className="mb-3">
                          <span 
                            className="inline-block px-2.5 py-1 text-white text-xs font-medium rounded-full"
                            style={{ backgroundColor: categoryColor }}
                          >
                            {article.category}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 
                          className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 transition-colors"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = categoryColor;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '';
                          }}
                        >
                          {article.title}
                        </h3>

                        {/* Description */}
                        {article.description && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed flex-1">
                            {article.description}
                          </p>
                        )}

                        {/* Meta Info */}
                        <div className="mt-auto pt-4 border-t border-gray-100">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span className="sr-only">Lesson details</span>
                            <span className="flex items-center gap-1">
                              {mode === "public" && !article.is_public ? (
                                <>
                                  Members only
                                </>
                              ) : (
                                <>
                                  <Clock className="h-3 w-3" />
                                  ~8 min
                                </>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
    </div>
  );

  if (withDashboard) {
    return <DashboardLayout>{content}</DashboardLayout>;
  }

  return content;
};

export default Learn;
